// Flat-cartoon UI primitives — bold ink outlines, cream "sticker" panels with a
// hard offset shadow, bright saturated buttons that press into their shadow.
// One look across the whole game (the Parent Dashboard keeps its own adult
// register and does not use these). Dark mode is a deferred palette pass; for
// now these use fixed bright-cartoon colours by design.

import { type CSSProperties, type ReactNode } from 'react';
import { motion } from 'framer-motion';

// Dark-neon studio tokens. The whole UI is a dark LED gamer room: deep-purple
// cards, LIGHT text, neon glow borders (electric blue / neon green / warm amber
// / pink). The bold Simpsons outline stays but GLOWS rather than inks.
export const INK = '#0b0a16'; // deep near-black — text/outline ON bright neon fills (buttons, Miko)
const PANEL = '#241b42'; // dark card surface
const PANEL_2 = '#2e2456';
const TEXT = '#f4f1ff'; // light text on dark
const TEXT_2 = '#cbc2ec';
/** neon accents — used for glowing borders + saturated button fills */
export const SCREEN = {
  blue: '#37b6ff',
  green: '#2fe08a',
  amber: '#ffba2b',
  pink: '#ff5c8a',
  /** default neutral panel border tint */
  glass: '#7b6bd6',
} as const;

/** Build a glowing neon border + soft outer glow for a dark card. */
function neonBox(color: string): CSSProperties {
  return {
    border: `2.5px solid ${color}`,
    boxShadow: `0 0 0 1px ${color}66, 0 0 18px ${color}55, 0 6px 18px rgba(0,0,0,0.5)`,
  };
}

// ---------- Card (dark neon panel) ------------------------------------------
type CardTone = 'glass' | 'blue' | 'green' | 'amber' | 'pink';
export function Card({
  children,
  style,
  pad = 18,
  className,
  tone = 'glass',
}: {
  children: ReactNode;
  style?: CSSProperties;
  pad?: number;
  className?: string;
  tone?: CardTone;
}) {
  return (
    <div
      className={className}
      style={{
        background: PANEL,
        borderRadius: 18,
        ...neonBox(SCREEN[tone]),
        padding: pad,
        color: TEXT,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ---------- Eyebrow / section label ----------------------------------------
export function Eyebrow({ children, color = TEXT_2 }: { children: ReactNode; color?: string }) {
  return (
    <div
      style={{
        fontSize: 11.5,
        fontWeight: 900,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color,
      }}
    >
      {children}
    </div>
  );
}

// ---------- Button ----------------------------------------------------------
type BtnTone = 'primary' | 'soft' | 'ghost' | 'up';
export function Button({
  children,
  onClick,
  tone = 'primary',
  size = 'md',
  full,
  disabled,
  style,
}: {
  children: ReactNode;
  onClick?: () => void;
  tone?: BtnTone;
  size?: 'sm' | 'md' | 'lg';
  full?: boolean;
  disabled?: boolean;
  style?: CSSProperties;
}) {
  const pad = size === 'lg' ? '0 22px' : size === 'sm' ? '0 12px' : '0 16px';
  const h = size === 'lg' ? 52 : size === 'sm' ? 36 : 46;
  const fs = size === 'lg' ? 17 : size === 'sm' ? 13 : 15;
  // Saturated neon fills glow; ghost is a dark panel with a neon border. Tap
  // dims the glow ("presses in").
  const fills: Record<BtnTone, string> = {
    primary: SCREEN.amber, // warm amber action
    up: SCREEN.green, // neon green
    soft: SCREEN.blue, // electric blue
    ghost: PANEL_2,
  };
  const fill = fills[tone];
  const ghost = tone === 'ghost';
  const edge = ghost ? SCREEN.blue : fill;
  return (
    <motion.button
      type="button"
      onClick={onClick}
      disabled={disabled}
      whileHover={disabled ? undefined : { y: -1 }}
      whileTap={disabled ? undefined : { y: 1, boxShadow: `0 0 0 1px ${edge}66, 0 0 6px ${edge}55` }}
      style={{
        height: h,
        padding: pad,
        width: full ? '100%' : undefined,
        borderRadius: 14,
        fontFamily: 'inherit',
        fontWeight: 900,
        fontSize: fs,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        background: fill,
        color: ghost ? TEXT : INK,
        border: `2.5px solid ${edge}`,
        boxShadow: `0 0 0 1px ${edge}66, 0 0 16px ${edge}66, 0 6px 16px rgba(0,0,0,0.5)`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        ...style,
      }}
    >
      {children}
    </motion.button>
  );
}

// ---------- Chip ------------------------------------------------------------
export function Chip({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'up' | 'down' | 'brand' }) {
  const accent = {
    neutral: SCREEN.amber,
    up: SCREEN.green,
    down: SCREEN.pink,
    brand: SCREEN.blue,
  }[tone];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '4px 11px',
        borderRadius: 999,
        background: `${accent}1f`,
        color: accent,
        border: `2px solid ${accent}`,
        boxShadow: `0 0 10px ${accent}40`,
        fontSize: 12,
        fontWeight: 900,
      }}
    >
      {children}
    </span>
  );
}

// ---------- Money -----------------------------------------------------------
/** Formats a play-money amount as B$, with optional sign + colour. */
export function money(n: number, opts: { sign?: boolean } = {}): string {
  const r = Math.round(n);
  const sign = r < 0 ? '−' : opts.sign && r > 0 ? '+' : '';
  return `${sign}B$${Math.abs(r).toLocaleString('en-US')}`;
}

export function Money({ value, signed, size = 18 }: { value: number; signed?: boolean; size?: number }) {
  const color = value > 0 ? 'var(--up)' : value < 0 ? 'var(--down)' : 'var(--text)';
  return (
    <span className="bk-tnum" style={{ color: signed ? color : 'var(--text)', fontWeight: 800, fontSize: size }}>
      {money(value, { sign: signed })}
    </span>
  );
}

// ---------- Metric tile -----------------------------------------------------
export function Metric({
  label,
  value,
  delta,
  accent = 'var(--text)',
}: {
  label: string;
  value: ReactNode;
  delta?: ReactNode;
  accent?: string;
}) {
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 700 }}>{label}</span>
      <span className="bk-tnum" style={{ fontSize: 22, fontWeight: 900, color: accent, lineHeight: 1.1 }}>
        {value}
      </span>
      {delta && <span style={{ fontSize: 12 }}>{delta}</span>}
    </div>
  );
}

// ---------- Progress bar (cartoon: ink-outlined capsule) --------------------
export function Bar({ frac, color = SCREEN.amber, track = '#0c1230', height = 12 }: { frac: number; color?: string; track?: string; height?: number }) {
  const pct = Math.round(Math.min(1, Math.max(0, frac)) * 100);
  // Map the old neo-fintech token colours to screen-glow equivalents so call
  // sites that still pass var(--up)/var(--brand) read on-style.
  const fill = color === 'var(--up)' ? SCREEN.green : color === 'var(--brand)' ? SCREEN.blue : color;
  return (
    <div style={{ height, borderRadius: 999, background: track, overflow: 'hidden', border: '1.5px solid rgba(255,255,255,0.10)', boxSizing: 'border-box' }}>
      <motion.div
        initial={false}
        animate={{ width: `${pct}%` }}
        transition={{ type: 'spring', stiffness: 120, damping: 22 }}
        style={{ height: '100%', background: fill, borderRadius: 999, boxShadow: `0 0 10px ${fill}aa` }}
      />
    </div>
  );
}

// ---------- Sparkbars (tiny weekly chart) -----------------------------------
export function Sparkbars({ values, height = 44 }: { values: number[]; height?: number }) {
  const max = Math.max(1, ...values.map((v) => Math.abs(v)));
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height }}>
      {values.map((v, i) => {
        const h = Math.max(3, (Math.abs(v) / max) * height);
        return (
          <div
            key={i}
            style={{
              flex: 1,
              height: h,
              borderRadius: 4,
              background: v >= 0 ? 'var(--up)' : 'var(--down)',
              opacity: 0.4 + (0.6 * (i + 1)) / values.length,
            }}
          />
        );
      })}
    </div>
  );
}

// ---------- Skyline (the growing empire) ------------------------------------
const STAGES = ['Side Hustle', 'Creator', 'Studio', 'Media House', 'Empire'];

export function empireStage(level: number): string {
  return STAGES[Math.min(STAGES.length - 1, Math.max(0, level - 1))]!;
}

/** Flat-vector skyline whose number/height of buildings grows with level. */
export function Skyline({ level, height = 96 }: { level: number; height?: number }) {
  const n = Math.min(8, 2 + level); // more buildings as you grow
  const buildings = Array.from({ length: n }, (_, i) => {
    const seed = (i * 73 + 13) % 100;
    const tall = (i + level) % 3 === 0;
    const w = 26 + (seed % 14);
    const h = (tall ? 0.62 : 0.34) * height + (seed % 22);
    return { w, h, lit: i % 2 === 0 };
  });
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 8, height, overflow: 'hidden' }}>
      {buildings.map((b, i) => (
        <motion.div
          key={i}
          initial={{ y: 14, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.04 * i, type: 'spring', stiffness: 200, damping: 20 }}
          style={{
            width: b.w,
            height: b.h,
            borderRadius: '8px 8px 0 0',
            background: i % 2 === 0 ? 'var(--brand)' : 'var(--brand-strong)',
            position: 'relative',
            boxShadow: 'var(--shadow-sm)',
          }}
        >
          {/* windows */}
          <div
            style={{
              position: 'absolute',
              inset: '8px 6px',
              backgroundImage:
                'repeating-linear-gradient(0deg, rgba(255,255,255,0.55) 0 3px, transparent 3px 9px), repeating-linear-gradient(90deg, rgba(255,255,255,0.55) 0 3px, transparent 3px 9px)',
              backgroundBlendMode: 'overlay',
              opacity: 0.5,
              borderRadius: 4,
            }}
          />
        </motion.div>
      ))}
    </div>
  );
}
